/**
 * Подписка на push-уведомления.
 *
 * Логика жила внутри панели водителя, и воронке она понадобилась целиком:
 * то же разрешение браузера, тот же ключ, тот же адрес подписки. Здесь
 * вынесено ровно то, что нужно обоим, без правки самой панели — она в
 * работе, и трогать её ради переезда кода незачем.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/* Запасной ключ, вшитый в код. На боевой он и работает: build-arg
   REACT_APP_VAPID_PUBLIC_KEY приходит из `.env` рядом с
   docker-compose.prod.yml, а этого файла там нет — arg пустой с 20.09.2026
   и раньше. Пока ключи совпадают, это незаметно; в день, когда ключ
   сменят в окружении бэкенда, фронт молча останется со старым, и
   единственным признаком будут неприходящие push. Поэтому подстановка
   говорит о себе в консоли, а не делает вид, что всё задано. */
const VAPID_FALLBACK = 'BJTzkaMvUO1s6uOYYjPwwi0UDpBvJpwHB0TYWFkNLGsrhbTxlVnJ2LEdaErMx0GdLBfjsQDqgvmHjuzAEuz7p9A';
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || VAPID_FALLBACK;
if (!process.env.REACT_APP_VAPID_PUBLIC_KEY) {
  console.warn(
    'REACT_APP_VAPID_PUBLIC_KEY не задан при сборке — взят запасной ключ из кода. ' +
    'Если push перестали приходить, сверьте его с VAPID_PUBLIC_KEY бэкенда.'
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

/** Уже подписан ли этот браузер. */
export async function pushStatus() {
  if (!pushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    return !!(await registration.pushManager.getSubscription());
  } catch {
    return false;
  }
}

/**
 * Подписать браузер. Возвращает { ok, error } — вызывающий сам решает,
 * показывать ли сообщение и каким тоном.
 */
export async function subscribeToPush() {
  if (!pushSupported()) {
    return { ok: false, error: 'Этот браузер не умеет push-уведомления' };
  }

  // На iPhone Safari разрешает уведомления только приложению, добавленному
  // на домашний экран. Без этой проверки пользователь получает невнятный
  // отказ вместо понятной инструкции.
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone;
  if (isIOS && !isStandalone) {
    return {
      ok: false,
      error: 'На iPhone: «Поделиться» → «На экран Домой», затем откройте приложение с главного экрана',
    };
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { ok: false, error: 'Разрешите уведомления в настройках браузера' };
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });

    const res = await fetch(`${API_URL}/api/notifications/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')))),
        },
      }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || `Сервер ответил ${res.status}`);
    }
    return { ok: true };
  } catch (e) {
    if (e.name === 'NotAllowedError') {
      return { ok: false, error: 'Разрешите уведомления в настройках браузера' };
    }
    if (e.name === 'NotSupportedError') {
      return { ok: false, error: 'Push-уведомления не поддерживаются в этом браузере' };
    }
    return { ok: false, error: e.message || 'Не удалось подписаться' };
  }
}

export async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return { ok: true };

    // Сначала снимаем на сервере: если браузер отпишется, а запись
    // останется, сервер будет слать в никуда и чистить её только по ошибке
    // доставки.
    await fetch(`${API_URL}/api/notifications/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
    }).catch(() => {});
    await subscription.unsubscribe();
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e.message || 'Не удалось отписаться' };
  }
}
